import { ArrowUpRight } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";
import { verfahrenLabels } from "@/lib/translations";

const BASE = "https://fritzkammag.ch/wp-content/uploads/2018/01";
const TROMMEL =
  "https://fritzkammag.ch/wp-content/uploads/2025/03/Trommelverzinken-blau-1030x684.jpg";

// Service descriptions per language; service titles use verfahrenLabels.
const DESCRIPTIONS = {
  de: {
    verzinken:
      "Klassischer Korrosionsschutz für Stahlbauteile durch elektrolytisches Aufbringen einer Zinkschicht. Robust, wirtschaftlich, langlebig.",
    trommelverzinken:
      "Ideal für hohe Stückzahlen kleiner und mittlerer Teile — gleichmässige Zinkbeschichtung im rotierenden Trommelverfahren.",
    "zink-nickel":
      "Hochwertiger Korrosionsschutz mit überlegener Beständigkeit — Standard in der Automobil- und Zulieferindustrie.",
    "zink-mangan-phosphat":
      "Phosphatschicht für verbesserte Reibungs- und Verschleisseigenschaften, optimal als Haftgrund für nachfolgende Beschichtungen.",
    "cns-beizen":
      "Reinigung und Aktivierung von Edelstahloberflächen — Entfernung von Anlauffarben und Zunder für blanke, passive Oberflächen.",
    "cns-elektropolieren":
      "Elektrochemisches Glätten von rostfreiem Stahl. Reinraumtaugliche, hygienische und korrosionsbeständige Hochglanzoberfläche.",
    "schleifen-buersten":
      "Mechanische Oberflächenbearbeitung — vom satinierten Finish bis zur dekorativen Strukturierung von Edelstahl und Buntmetallen.",
    bruenieren:
      "Tiefschwarze Konversionsschicht auf Stahl. Dezentes Erscheinungsbild bei minimalem Schichtauftrag — funktional und ästhetisch.",
    vergolden:
      "Galvanische Goldveredelung für Funktion und Ästhetik — von der dünnen Kontaktschicht bis zur dekorativen Hochglanzbeschichtung.",
    "chemisch-nickel":
      "Stromlose Vernicklung mit gleichmässiger Schichtdicke auf komplexen Geometrien. Hart, korrosionsbeständig, präzise.",
    "galvanisch-nickel":
      "Klassische galvanische Vernicklung — dekorativ, korrosionshemmend und Basis für vielfältige weitere Veredelungen.",
    intro:
      "Vom klassischen Verzinken bis zum CNS-Elektropolieren — jedes Verfahren wird in Mels nach Swiss Safety Center Standards ausgeführt und dokumentiert. Je nach Auftragsgrösse und Kapazität platzieren wir Ihre Bestellung passend und beraten zu weiteren Möglichkeiten.",
  },
  en: {
    verzinken:
      "Classic corrosion protection for steel parts through electrolytic zinc coating. Robust, economical, long-lasting.",
    trommelverzinken:
      "Ideal for high volumes of small and medium-sized parts — uniform zinc coating using a rotating barrel process.",
    "zink-nickel":
      "Premium corrosion protection with superior durability — standard for automotive and tier-supplier applications.",
    "zink-mangan-phosphat":
      "Phosphate layer for improved friction and wear properties — an ideal base coat for subsequent finishes.",
    "cns-beizen":
      "Cleaning and activation of stainless-steel surfaces — removing heat tint and scale for a bright, passive finish.",
    "cns-elektropolieren":
      "Electrochemical smoothing of stainless steel. A cleanroom-grade, hygienic, corrosion-resistant high-gloss finish.",
    "schleifen-buersten":
      "Mechanical surface treatment — from satin finishes to decorative texturing of stainless steel and non-ferrous metals.",
    bruenieren:
      "Deep-black conversion coating on steel. Subtle appearance with minimal build-up — both functional and aesthetic.",
    vergolden:
      "Electrolytic gold finishing for function and aesthetics — from thin contact layers to decorative high-gloss plating.",
    "chemisch-nickel":
      "Electroless nickel plating with uniform thickness on complex geometries. Hard, corrosion-resistant, precise.",
    "galvanisch-nickel":
      "Classic electrolytic nickel plating — decorative, corrosion-inhibiting and a foundation for further finishes.",
    intro:
      "From classic electrogalvanizing to stainless-steel electropolishing — every process is executed and documented in Mels to Swiss Safety Center standards. Depending on volume and capacity, we route your order appropriately and advise on further options.",
  },
  fr: {
    verzinken:
      "Protection anti-corrosion classique pour pièces acier par dépôt électrolytique de zinc. Robuste, économique, durable.",
    trommelverzinken:
      "Idéal pour de grandes quantités de petites et moyennes pièces — revêtement de zinc uniforme par tonneau rotatif.",
    "zink-nickel":
      "Protection anti-corrosion premium à durabilité supérieure — standard pour l'automobile et la sous-traitance de premier rang.",
    "zink-mangan-phosphat":
      "Couche de phosphate améliorant le frottement et la résistance à l'usure — base d'accrochage idéale pour des finitions ultérieures.",
    "cns-beizen":
      "Nettoyage et activation des surfaces inox — élimination des colorations thermiques et de la calamine pour une finition brillante et passive.",
    "cns-elektropolieren":
      "Lissage électrochimique de l'inox. Finition haute brillance, hygiénique et résistante à la corrosion, compatible salle blanche.",
    "schleifen-buersten":
      "Traitement mécanique de surface — du satinage aux structures décoratives sur inox et métaux non ferreux.",
    bruenieren:
      "Couche de conversion noir profond sur acier. Aspect discret avec un dépôt minimal — à la fois fonctionnel et esthétique.",
    vergolden:
      "Dorure galvanique fonctionnelle ou esthétique — de la fine couche de contact au revêtement décoratif haute brillance.",
    "chemisch-nickel":
      "Nickelage chimique d'épaisseur uniforme sur géométries complexes. Dur, anti-corrosion, précis.",
    "galvanisch-nickel":
      "Nickelage électrolytique classique — décoratif, anti-corrosion et base pour de nombreuses finitions ultérieures.",
    intro:
      "Du zingage classique au polissage électrolytique de l'inox — chaque procédé est exécuté et documenté à Mels selon les standards Swiss Safety Center. Selon la quantité et la capacité, nous orientons votre commande et conseillons des options complémentaires.",
  },
  it: {
    verzinken:
      "Protezione anticorrosiva classica per componenti in acciaio mediante deposito elettrolitico di zinco. Robusta, economica, durevole.",
    trommelverzinken:
      "Ideale per grandi quantità di piccoli e medi componenti — rivestimento di zinco uniforme nel processo a rotobarile.",
    "zink-nickel":
      "Protezione anticorrosiva premium con resistenza superiore — standard nell'automotive e nella subfornitura.",
    "zink-mangan-phosphat":
      "Strato di fosfato per migliori proprietà di attrito e usura — base d'aggrappo ideale per rivestimenti successivi.",
    "cns-beizen":
      "Pulizia e attivazione delle superfici inox — rimozione di ossidazioni termiche e calamine per superfici brillanti e passive.",
    "cns-elektropolieren":
      "Lisciatura elettrochimica dell'acciaio inox. Superficie a specchio, igienica, resistente alla corrosione, idonea a camere bianche.",
    "schleifen-buersten":
      "Trattamento meccanico delle superfici — dal satinato alla strutturazione decorativa di inox e metalli non ferrosi.",
    bruenieren:
      "Strato di conversione nero profondo su acciaio. Aspetto discreto con deposito minimo — funzionale ed estetico.",
    vergolden:
      "Doratura galvanica per funzione ed estetica — dal sottile strato di contatto al rivestimento decorativo a specchio.",
    "chemisch-nickel":
      "Nichelatura chimica con spessore uniforme anche su geometrie complesse. Dura, anticorrosiva, precisa.",
    "galvanisch-nickel":
      "Nichelatura galvanica classica — decorativa, anticorrosiva e base per ulteriori finiture.",
    intro:
      "Dalla zincatura classica all'elettrolucidatura dell'inox — ogni procedimento viene eseguito e documentato a Mels secondo gli standard Swiss Safety Center. In base al volume e alla capacità, indirizziamo il Suo ordine e consigliamo opzioni aggiuntive.",
  },
};

const services = [
  { id: "verzinken", n: "01", titleKey: "Verzinken", image: `${BASE}/Verzinken-1.jpg`, feature: true },
  { id: "trommelverzinken", n: "02", titleKey: "Trommelverzinken", image: TROMMEL },
  { id: "zink-nickel", n: "03", titleKey: "Zink-Nickel", image: `${BASE}/Zink-Nickel-1.png` },
  { id: "zink-mangan-phosphat", n: "04", titleKey: "Zink-Mangan-Phosphatieren", image: `${BASE}/zink-Mangan-Phosphatieren-1.jpg` },
  { id: "cns-beizen", n: "05", titleKey: "CNS-Beizen", image: `${BASE}/cns-Beizen-1.png` },
  { id: "cns-elektropolieren", n: "06", titleKey: "CNS-Elektropolieren", image: `${BASE}/cns-Elektropolieren-1.png` },
  { id: "schleifen-buersten", n: "07", titleKey: "Schleifen · Bürsten", image: `${BASE}/Schleifen-B%C3%BCrsten-1.png` },
  { id: "bruenieren", n: "08", titleKey: "Brünieren", image: `${BASE}/Br%C3%BCnieren-2.png` },
  { id: "vergolden", n: "09", titleKey: "Vergolden", image: `${BASE}/Vergolden-2.png` },
  { id: "chemisch-nickel", n: "10", titleKey: "Chemisch-Nickel", image: `${BASE}/Chemisch-Nickel-1.png` },
  { id: "galvanisch-nickel", n: "11", titleKey: "Galvanisch-Nickel", image: `${BASE}/Galvanisch-Nickel-1.png` },
];

const HAUPTVERFAHREN = { de: "Hauptverfahren", en: "Lead process", fr: "Procédé principal", it: "Procedimento principale" };

export const Services = () => {
  const t = useT();
  const { lang } = useI18n();
  const labelMap = verfahrenLabels[lang] || verfahrenLabels.de;
  const desc = DESCRIPTIONS[lang] || DESCRIPTIONS.de;
  const tr = (titleKey) => labelMap[titleKey] || titleKey;
  const [hero, ...rest] = services;
  return (
    <section
      id="verfahren"
      data-testid="services-section"
      className="relative bg-[#0E0E10] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-5">
            <div className="overline reveal mb-5">{t("sections.verfahren_overline", "— Verfahren")} · 02 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.verfahren_title_1", "Elf Verfahren.")}
              <br />
              <span className="text-zinc-500">{t("sections.verfahren_title_2", "Ein Massstab.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-xl">
              {desc.intro}
            </p>
          </div>
        </div>

        {/* Hero feature card */}
        <article
          data-testid={`service-card-${hero.id}`}
          className="reveal group relative h-[440px] md:h-[520px] mb-px overflow-hidden border border-white/10"
        >
          <img
            src={hero.image}
            alt={tr(hero.titleKey)}
            className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-[1400ms] ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-transparent" />
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative h-full p-8 md:p-14 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="overline text-zinc-300">{hero.n} · {HAUPTVERFAHREN[lang] || HAUPTVERFAHREN.de}</span>
              <div className="w-11 h-11 border border-white/30 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors duration-500">
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </div>
            </div>
            <div className="max-w-2xl">
              <h3 className="font-display text-4xl md:text-6xl font-semibold tracking-tight text-white mb-5">
                {tr(hero.titleKey)}
              </h3>
              <p className="text-zinc-200 text-base md:text-lg leading-relaxed">
                {desc[hero.id]}
              </p>
            </div>
          </div>
        </article>

        {/* Grid of remaining services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border-x border-b border-white/10">
          {rest.map((s, i) => (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className={`reveal reveal-delay-${(i % 5) + 1} group relative bg-[#0E0E10] hover:bg-[#161618] transition-colors duration-500 overflow-hidden`}
            >
              <div className="relative aspect-[4/3] overflow-hidden border-b border-white/10">
                <img
                  src={s.image}
                  alt={tr(s.titleKey)}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-95 group-hover:scale-105 transition-all duration-[1200ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E10]/70 to-transparent" />
                <span className="absolute top-4 left-4 overline text-white/90">
                  {s.n}
                </span>
              </div>
              <div className="p-6 md:p-8">
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-white mb-3">
                  {tr(s.titleKey)}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{desc[s.id]}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
